/**
 * Helper pro ukládání a načítání zoom nastavení grafů do localStorage
 */

import moment from 'moment';

export interface ChartZoomState {
    viewStart: number | null;
    viewEnd: number | null;
    granularity: 'day' | 'week' | 'month';
}

/**
 * Relativní zoom state - ukládá offset od začátku/konce místo absolutních timestampů
 */
export interface RelativeChartZoomState {
    /** Počet jednotek (dnů/týdnů/měsíců) zobrazených od konce dat */
    visibleUnits: number;
    granularity: 'day' | 'week' | 'month';
}

export interface CategoryZoomState {
    startIndex: number | null;
    endIndex: number | null;
    granularity: 'day' | 'week' | 'month';
}

// Změněn prefix pro reset všech zoomů na nový default (zobrazit všechna data)
const STORAGE_PREFIX = 'tpm.chart.zoom.v3.';

/**
 * Uloží relativní zoom nastavení pro časový graf
 * @param chartId ID grafu
 * @param granularity Aktuální granularita
 * @param viewStart Absolutní timestamp začátku zobrazeného rozsahu
 * @param viewEnd Absolutní timestamp konce zobrazeného rozsahu
 * @param dataStart Absolutní timestamp prvního datového bodu
 * @param dataEnd Absolutní timestamp posledního datového bodu
 * @param timeUnit Jednotka času ('day', 'isoWeek', 'month')
 */
export function saveChartZoom(
    chartId: string,
    granularity: 'day' | 'week' | 'month',
    viewStart: number,
    viewEnd: number,
    dataStart: number,
    dataEnd: number,
    timeUnit: 'day' | 'isoWeek' | 'month'
): void {
    // Ukládání do localStorage je vypnuto - vždy se zobrazí všechna data
    return;
}

/**
 * Načte relativní zoom nastavení pro časový graf a přepočítá na absolutní hodnoty
 * @param chartId ID grafu
 * @param currentGranularity Aktuální granularita
 * @param dataStart Absolutní timestamp prvního datového bodu
 * @param dataEnd Absolutní timestamp posledního datového bodu
 * @param timeUnit Jednotka času ('day', 'isoWeek', 'month')
 */
export function loadChartZoom(
    chartId: string,
    currentGranularity: 'day' | 'week' | 'month',
    dataStart: number,
    dataEnd: number,
    timeUnit: 'day' | 'isoWeek' | 'month'
): ChartZoomState | null {
    // Načítání z localStorage je vypnuto - vždy se zobrazí všechna data
    return null;
}

/**
 * Uloží zoom nastavení pro kategoriální graf (s startIndex/endIndex)
 */
export function saveCategoryZoom(chartId: string, state: CategoryZoomState): void {
    try {
        const key = STORAGE_PREFIX + chartId;
        localStorage.setItem(key, JSON.stringify(state));
    } catch (e) {
        console.warn('Failed to save category zoom to localStorage', e);
    }
}

/**
 * Načte zoom nastavení pro kategoriální graf
 */
export function loadCategoryZoom(chartId: string, currentGranularity: 'day' | 'week' | 'month'): CategoryZoomState | null {
    try {
        const key = STORAGE_PREFIX + chartId;
        const raw = localStorage.getItem(key);
        if (!raw) return null;

        const state = JSON.parse(raw) as CategoryZoomState;
        
        // Pokud se změnila granularita, vrátíme null (použije se výchozí zoom)
        if (state.granularity !== currentGranularity) {
            return null;
        }

        return state;
    } catch (e) {
        console.warn('Failed to load category zoom from localStorage', e);
        return null;
    }
}

/**
 * Vymaže zoom nastavení pro daný graf
 */
export function clearChartZoom(chartId: string): void {
    try {
        const key = STORAGE_PREFIX + chartId;
        localStorage.removeItem(key);
    } catch (e) {
        console.warn('Failed to clear chart zoom from localStorage', e);
    }
}

/**
 * Vymaže všechny uložené zoom nastavení
 */
export function clearAllChartZooms(): void {
    try {
        const keys = Object.keys(localStorage);
        for (const key of keys) {
            if (key.startsWith(STORAGE_PREFIX)) {
                localStorage.removeItem(key);
            }
        }
    } catch (e) {
        console.warn('Failed to clear all chart zooms from localStorage', e);
    }
}

/**
 * Vrátí optimální výchozí počet položek pro danou granularitu
 * Nastaveno na zobrazení co nejvíce dat najednou (nejmenší zoom)
 * Vrací velmi velké číslo, aby se zobrazila VŠECHNA dostupná data
 */
export function getOptimalWindowSize(_granularity: 'day' | 'week' | 'month'): number {
    // Vrátíme velmi velké číslo, aby se vždy zobrazila všechna dostupná data
    // V resetViewport() se pak použije Math.min(windowDays, actual data length)
    return 99999;
}

